import React, { useState, useRef, useCallback } from 'react';
import {
  Plus,
  ClipboardList,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  AlertCircle,
  Trash2,
  Eye,
  FileText,
  Upload,
  ChevronRight,
  Edit2,
  Download,
  AlertTriangle,
  X,
  Save,
  ExternalLink,
  Loader2,
  Award,
  Scale,
  Paperclip,
  Bold,
  Italic,
  List,
  ListOrdered,
  Table,
  Settings,
  GraduationCap,
  Maximize2,
  FileSpreadsheet,
  File,
  Image,
} from 'lucide-react';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { createSafeHtml } from '@/utils/sanitize';
import {
  useAssignments,
  useAssignmentDetail,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  useGradeSubmission,
  useGradeConfig,
} from '../../hooks/useProfessor';
import type {
  AssignmentResponse,
  SubmissionResponse,
  CreateAssignmentDto,
  UpdateAssignmentDto,
  GradeSubmissionDto,
  AttachmentFile,
} from '@/types/professor.types';
import { uploadAssignmentFile } from '../../services/professorService';
import toast from 'react-hot-toast';

// API base URL for file access
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}`;

// Convert relative URL to full URL
const getFullUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${API_BASE_URL}${url}`;
  }
  return url;
};

// File preview state for expanded view
interface FilePreviewState {
  isExpanded: boolean;
  fileUrl: string | null;
  fileName: string | null;
  loading: boolean;
  error: string | null;
  wordContent: string | null;
  excelData: { sheets: { name: string; data: string[][] }[]; activeSheet: number } | null;
}

// Simple Rich Text Editor Component
interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder, minRows = 4 }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastExternalValueRef = useRef<string | null>(null);
  const isInitialMount = useRef(true);

  // Update content when value changes from outside (e.g., form reset or switching to edit mode)
  React.useEffect(() => {
    if (editorRef.current) {
      // On initial mount, always set the content
      if (isInitialMount.current) {
        editorRef.current.innerHTML = value || '';
        lastExternalValueRef.current = value || '';
        isInitialMount.current = false;
        return;
      }

      // After initial mount, only update if the external value is different
      // This happens when switching from create to edit mode
      if (value !== lastExternalValueRef.current) {
        editorRef.current.innerHTML = value || '';
        lastExternalValueRef.current = value || '';
      }
    }
  }, [value]);

  const execCommand = (command: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      lastExternalValueRef.current = newContent;
      onChange(newContent);
    }
  };

  const handleInput = () => {
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      lastExternalValueRef.current = newContent;
      onChange(newContent);
    }
  };

  const insertTable = () => {
    editorRef.current?.focus();
    const table = `<table style="border-collapse: collapse; width: 100%; margin: 8px 0;">
      <tr>
        <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Critère</th>
        <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Points</th>
        <th style="border: 1px solid #ddd; padding: 8px; background: #f5f5f5;">Description</th>
      </tr>
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
      </tr>
      <tr>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
        <td style="border: 1px solid #ddd; padding: 8px;"></td>
      </tr>
    </table><p></p>`;
    document.execCommand('insertHTML', false, table);
    if (editorRef.current) {
      const newContent = editorRef.current.innerHTML;
      lastExternalValueRef.current = newContent;
      onChange(newContent);
    }
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('bold')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Gras"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('italic')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Italique"
        >
          <Italic className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('insertUnorderedList')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Liste à puces"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => execCommand('insertOrderedList')}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Liste numérotée"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={insertTable}
          className="p-1.5 hover:bg-gray-200 rounded transition-colors"
          title="Insérer un tableau"
        >
          <Table className="h-4 w-4" />
        </button>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-3 min-h-[120px] focus:outline-none prose prose-sm max-w-none"
        style={{ minHeight: `${minRows * 24}px` }}
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
      <style>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          display: block;
        }
        [contenteditable] table, .prose table, .rendered-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 12px 0;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e5e7eb;
        }
        [contenteditable] th, [contenteditable] td, .prose th, .prose td, .rendered-content th, .rendered-content td {
          border: 1px solid #e5e7eb;
          padding: 10px 14px;
          text-align: left;
        }
        [contenteditable] th, .prose th, .rendered-content th {
          background: linear-gradient(to bottom, #f9fafb, #f3f4f6);
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
        }
        .prose tr:nth-child(even), .rendered-content tr:nth-child(even) {
          background-color: #f9fafb;
        }
        .prose tr:hover, .rendered-content tr:hover {
          background-color: #f3f4f6;
        }
        .prose ul, .prose ol, .rendered-content ul, .rendered-content ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .prose li, .rendered-content li {
          margin: 0.25rem 0;
        }
        .prose p, .rendered-content p {
          margin: 0.5rem 0;
        }
      `}</style>
    </div>
  );
};

// Drag & Drop File Upload Component
interface FileUploadZoneProps {
  onFileSelect: (file: { name: string; url: string; type?: string; size?: number }) => void;
  existingFiles?: AttachmentFile[];
  onRemoveFile?: (index: number) => void;
  multiple?: boolean;
  accept?: string;
  hint?: string;
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  onFileSelect,
  existingFiles = [],
  onRemoveFile,
  multiple = true,
  accept,
  hint = "Glissez-déposez vos fichiers ici ou cliquez pour sélectionner"
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadToServer = useCallback(async (file: File) => {
    setIsUploading(true);
    try {
      // Upload to server and get persistent URL
      const response = await uploadAssignmentFile(file);
      setIsUploading(false);
      return {
        name: response.file.name,
        url: response.file.url,
        type: response.file.type,
        size: response.file.size
      };
    } catch (error) {
      setIsUploading(false);
      toast.error('Erreur lors du téléchargement du fichier');
      throw error;
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files) as File[];
    for (const file of files) {
      try {
        const uploaded = await uploadToServer(file);
        onFileSelect({ name: uploaded.name, url: uploaded.url, type: uploaded.type, size: uploaded.size });
        if (!multiple) break;
      } catch {
        // Error already handled in uploadToServer
      }
    }
  }, [onFileSelect, multiple, uploadToServer]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    for (const file of files) {
      try {
        const uploaded = await uploadToServer(file);
        onFileSelect({ name: uploaded.name, url: uploaded.url, type: uploaded.type, size: uploaded.size });
        if (!multiple) break;
      } catch {
        // Error already handled in uploadToServer
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      {/* File List - shown first */}
      {existingFiles.length > 0 && (
        <div className="space-y-2">
          {existingFiles.map((file, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
              <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <span className="flex-1 text-sm text-gray-700 truncate">{file.fileName}</span>
              {file.fileSize && (
                <span className="text-xs text-gray-400">
                  {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                </span>
              )}
              <a
                href={getFullUrl(file.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-4 w-4" />
              </a>
              {onRemoveFile && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                  className="p-1 text-red-600 hover:bg-red-50 rounded"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
          ${isDragging
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />

        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            <p className="text-sm text-gray-600">Téléchargement en cours...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className={`h-8 w-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
            <p className="text-sm text-gray-600">{hint}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Single file upload zone for Rubric
interface RubricUploadZoneProps {
  onFileSelect: (file: { name: string; url: string; type?: string; size?: number }) => void;
  hasFile: boolean;
}

const RubricUploadZone: React.FC<RubricUploadZoneProps> = ({ onFileSelect, hasFile }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const uploadToServer = useCallback(async (file: File) => {
    setIsUploading(true);
    try {
      const response = await uploadAssignmentFile(file);
      setIsUploading(false);
      return { name: response.file.name, url: response.file.url, type: response.file.type, size: response.file.size };
    } catch (error) {
      setIsUploading(false);
      toast.error('Erreur lors du téléchargement du fichier');
      throw error;
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files) as File[];
    if (files.length > 0) {
      try {
        const uploaded = await uploadToServer(files[0]);
        onFileSelect(uploaded);
      } catch {
        // Error handled in uploadToServer
      }
    }
  }, [onFileSelect, uploadToServer]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length > 0) {
      try {
        const uploaded = await uploadToServer(files[0]);
        onFileSelect(uploaded);
      } catch {
        // Error handled in uploadToServer
      }
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (hasFile) return null;

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      className={`
        border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-all
        ${isDragging
          ? 'border-purple-500 bg-purple-50'
          : 'border-purple-200 hover:border-purple-300 hover:bg-purple-50/50'
        }
      `}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx"
        onChange={handleFileChange}
        className="hidden"
      />
      {isUploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-6 w-6 text-purple-600 animate-spin" />
          <p className="text-sm text-gray-600">Téléchargement en cours...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1">
          <Upload className={`h-6 w-6 ${isDragging ? 'text-purple-600' : 'text-purple-400'}`} />
          <p className="text-sm text-gray-600">Glissez-déposez ou cliquez pour sélectionner</p>
        </div>
      )}
    </div>
  );
};

// Assignment Detail View with Inline Preview Component
interface AssignmentDetailViewInlineProps {
  assignmentDetail: any;
  selectedAssignmentId: string;
  onBack: () => void;
  onGrade: () => void;
}

const AssignmentDetailViewInline: React.FC<AssignmentDetailViewInlineProps> = ({
  assignmentDetail,
  selectedAssignmentId: _selectedAssignmentId,
  onBack,
  onGrade,
}) => {
  // selectedAssignmentId is passed for interface consistency but not directly used in render
  void _selectedAssignmentId;
  // File preview state
  const [expandedPreview, setExpandedPreview] = useState<FilePreviewState>({
    isExpanded: false,
    fileUrl: null,
    fileName: null,
    loading: false,
    error: null,
    wordContent: null,
    excelData: null,
  });

  // Combine all attachments (legacy single + array)
  const allAttachments: AttachmentFile[] = [
    ...(assignmentDetail.attachmentUrl ? [{
      url: assignmentDetail.attachmentUrl,
      fileName: assignmentDetail.attachmentFileName || 'Document joint'
    }] : []),
    ...(assignmentDetail.attachments || [])
  ];

  // Get file extension
  const getFileExtension = (name: string | null | undefined): string => {
    if (!name) return '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  // Check file types
  const isWordDocument = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['doc', 'docx'].includes(ext);
  };

  const isExcelDocument = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['xls', 'xlsx', 'csv'].includes(ext);
  };

  const isPdfDocument = (name: string | null | undefined): boolean => {
    return getFileExtension(name) === 'pdf';
  };

  const isImageFile = (name: string | null | undefined): boolean => {
    const ext = getFileExtension(name);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext);
  };

  // Get file icon
  const getFileIcon = (name: string | null | undefined) => {
    const ext = getFileExtension(name);
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return Image;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['pdf'].includes(ext)) return FileText;
    return File;
  };

  // Get file color
  const getFileColor = (name: string | null | undefined) => {
    const ext = getFileExtension(name);
    if (['pdf'].includes(ext)) return 'text-red-600 bg-red-100';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'text-pink-600 bg-pink-100';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'text-green-600 bg-green-100';
    if (['doc', 'docx'].includes(ext)) return 'text-blue-600 bg-blue-100';
    if (['ppt', 'pptx'].includes(ext)) return 'text-orange-600 bg-orange-100';
    return 'text-gray-600 bg-gray-100';
  };

  // Format file size
  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // Fetch file as ArrayBuffer (works for both data: URLs and server URLs)
  const fetchAsArrayBuffer = async (url: string): Promise<ArrayBuffer> => {
    const fullUrl = getFullUrl(url);
    if (fullUrl.startsWith('data:')) {
      const base64Data = fullUrl.includes(',') ? fullUrl.split(',')[1] : fullUrl;
      const binaryString = window.atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes.buffer;
    } else {
      const response = await fetch(fullUrl);
      return response.arrayBuffer();
    }
  };

  // Load Word document
  const loadWordDocument = useCallback(async (url: string) => {
    try {
      const arrayBuffer = await fetchAsArrayBuffer(url);
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        wordContent: result.value,
        error: null,
      }));
    } catch (err) {
      console.error('Error loading Word document:', err);
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        error: 'Impossible de charger le document Word.',
      }));
    }
  }, []);

  // Load Excel document
  const loadExcelDocument = useCallback(async (url: string) => {
    try {
      const arrayBuffer = await fetchAsArrayBuffer(url);
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });
        return { name, data: jsonData as string[][] };
      });

      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        excelData: { sheets, activeSheet: 0 },
        error: null,
      }));
    } catch (err) {
      console.error('Error loading Excel document:', err);
      setExpandedPreview(prev => ({
        ...prev,
        loading: false,
        error: 'Impossible de charger le fichier Excel.',
      }));
    }
  }, []);

  // Expand/preview a file inline
  const handleExpandFile = useCallback(async (url: string, name: string) => {
    const fullUrl = getFullUrl(url);

    setExpandedPreview({
      isExpanded: true,
      fileUrl: fullUrl,
      fileName: name,
      loading: true,
      error: null,
      wordContent: null,
      excelData: null,
    });

    if (isWordDocument(name)) {
      await loadWordDocument(url);
    } else if (isExcelDocument(name)) {
      await loadExcelDocument(url);
    } else {
      setExpandedPreview(prev => ({ ...prev, loading: false }));
    }
  }, [loadWordDocument, loadExcelDocument]);

  // Close expanded preview
  const handleClosePreview = () => {
    setExpandedPreview({
      isExpanded: false,
      fileUrl: null,
      fileName: null,
      loading: false,
      error: null,
      wordContent: null,
      excelData: null,
    });
  };

  // Handle file download
  const handleDownload = async (url: string, name: string) => {
    try {
      const fullUrl = getFullUrl(url);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = name || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Téléchargement démarré');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Render expanded preview content inline
  const renderExpandedPreviewInline = () => {
    if (!expandedPreview.isExpanded || !expandedPreview.fileUrl) return null;

    return (
      <div className="bg-gray-100 rounded-b-xl border-x-2 border-b-2 border-blue-300 overflow-hidden">
        {/* Compact header with just close button */}
        <div className="flex items-center justify-end p-2 bg-gray-50 border-b border-gray-200">
          <button
            onClick={handleClosePreview}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <X className="h-4 w-4" />
            Fermer
          </button>
        </div>

        {/* Preview Content */}
        <div className="p-4 min-h-[300px] max-h-[500px] overflow-auto">
          {expandedPreview.loading && (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Chargement du document...</p>
            </div>
          )}

          {expandedPreview.error && (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <AlertCircle className="h-16 w-16 mb-4 text-amber-500" />
              <p className="text-lg font-medium">Erreur de chargement</p>
              <p className="text-sm mt-1">{expandedPreview.error}</p>
              <button
                onClick={() => handleDownload(expandedPreview.fileUrl!, expandedPreview.fileName || 'document')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Télécharger pour visualiser
              </button>
            </div>
          )}

          {/* PDF Preview */}
          {isPdfDocument(expandedPreview.fileName) && !expandedPreview.loading && !expandedPreview.error && (
            <iframe
              src={expandedPreview.fileUrl}
              className="w-full h-[450px] rounded-lg border border-gray-200 bg-white"
              title={expandedPreview.fileName || 'PDF'}
            />
          )}

          {/* Image Preview */}
          {isImageFile(expandedPreview.fileName) && !expandedPreview.loading && !expandedPreview.error && (
            <div className="flex items-center justify-center bg-white p-4 rounded-lg">
              <img
                src={expandedPreview.fileUrl}
                alt={expandedPreview.fileName || 'Image'}
                className="max-w-full max-h-[400px] object-contain rounded-lg shadow-lg"
              />
            </div>
          )}

          {/* Word Preview */}
          {isWordDocument(expandedPreview.fileName) && expandedPreview.wordContent && !expandedPreview.loading && (
            <div className="bg-white p-8 rounded-lg shadow-sm">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={createSafeHtml(expandedPreview.wordContent)}
              />
            </div>
          )}

          {/* Excel Preview */}
          {isExcelDocument(expandedPreview.fileName) && expandedPreview.excelData && !expandedPreview.loading && (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Sheet tabs */}
              {expandedPreview.excelData.sheets.length > 1 && (
                <div className="flex gap-1 p-2 bg-gray-200 border-b overflow-x-auto">
                  {expandedPreview.excelData.sheets.map((sheet, index) => (
                    <button
                      key={sheet.name}
                      onClick={() => setExpandedPreview(prev => ({
                        ...prev,
                        excelData: prev.excelData ? { ...prev.excelData, activeSheet: index } : null
                      }))}
                      className={`px-3 py-1.5 text-sm rounded-t-lg whitespace-nowrap transition-colors ${
                        index === expandedPreview.excelData!.activeSheet
                          ? 'bg-white text-green-700 font-medium'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Table */}
              <div className="overflow-auto max-h-[400px]">
                <table className="border-collapse min-w-full">
                  <tbody>
                    {expandedPreview.excelData.sheets[expandedPreview.excelData.activeSheet].data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'bg-green-50 font-medium sticky top-0' : 'hover:bg-gray-50'}>
                        <td className="px-2 py-1 text-xs text-gray-400 bg-gray-100 border border-gray-200 text-center min-w-[40px] sticky left-0">
                          {rowIndex + 1}
                        </td>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`px-3 py-2 border border-gray-200 text-sm ${
                              rowIndex === 0 ? 'bg-green-50 font-medium text-green-900' : 'text-gray-700'
                            }`}
                          >
                            {cell !== undefined && cell !== null ? String(cell) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Check if a specific file is currently expanded
  const isFileExpanded = (url: string) => {
    return expandedPreview.isExpanded && getFullUrl(url) === expandedPreview.fileUrl;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <button
          onClick={onBack}
          className="text-sm text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-1"
        >
          <ChevronRight className="h-4 w-4 rotate-180" />
          Retour aux devoirs
        </button>

        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-2xl font-semibold text-gray-900">{assignmentDetail.title}</h3>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Date limite: {new Date(assignmentDetail.dueDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="flex items-center gap-1">
                <Award className="h-4 w-4" />
                Note max: {assignmentDetail.maxScore}
              </span>
              {assignmentDetail.allowLateSubmission && (
                <span className="flex items-center gap-1 text-amber-600">
                  <Clock className="h-4 w-4" />
                  Retards: {assignmentDetail.lateSubmissionDays}j (-{assignmentDetail.latePenaltyPercent}%/j)
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                {assignmentDetail.submissionsCount}/{assignmentDetail.totalStudents} soumissions
              </span>
              <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm">
                {assignmentDetail.gradedCount} notés
              </span>
            </div>
            <button
              onClick={onGrade}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <GraduationCap className="h-5 w-5" />
              Corriger
            </button>
          </div>
        </div>
      </div>

      {/* Instructions Section */}
      {assignmentDetail.instructions && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-blue-600" />
            <h4 className="font-semibold text-gray-900">Instructions</h4>
          </div>
          <div
            className="rendered-content prose prose-sm max-w-none text-gray-700"
            dangerouslySetInnerHTML={createSafeHtml(assignmentDetail.instructions)}
          />
        </div>
      )}

      {/* Attachments Section with Inline Preview */}
      {allAttachments.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip className="h-5 w-5 text-green-600" />
            <h4 className="font-semibold text-gray-900">Documents joints</h4>
            <span className="text-sm text-gray-400">({allAttachments.length} fichier{allAttachments.length > 1 ? 's' : ''})</span>
          </div>
          <div className="space-y-3">
            {allAttachments.map((file, index) => {
              const Icon = getFileIcon(file.fileName);
              const colorClass = getFileColor(file.fileName);
              const ext = getFileExtension(file.fileName);
              const isCurrentlyExpanded = isFileExpanded(file.url);

              return (
                <div key={index}>
                  <div
                    className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                      isCurrentlyExpanded
                        ? 'bg-blue-50 border-2 border-blue-300'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`p-3 rounded-lg ${colorClass}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900">{file.fileName || 'Document sans nom'}</p>
                      <p className="text-xs text-gray-500">
                        {ext ? ext.toUpperCase() + ' Document' : 'Fichier'}
                        {file.fileSize ? ` • ${formatFileSize(file.fileSize)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {!isCurrentlyExpanded && (
                        <button
                          onClick={() => handleExpandFile(file.url, file.fileName)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                        >
                          <Maximize2 className="h-4 w-4" />
                          Voir
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file.url, file.fileName)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <Download className="h-4 w-4" />
                        Télécharger
                      </button>
                    </div>
                  </div>

                  {/* Inline expanded preview for this specific file */}
                  {isCurrentlyExpanded && renderExpandedPreviewInline()}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Rubric Section with Inline Preview */}
      {(assignmentDetail.rubricContent || assignmentDetail.rubricUrl) && (
        <div className="bg-white rounded-xl border-2 border-purple-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="h-5 w-5 text-purple-600" />
            <h4 className="font-semibold text-gray-900">Grille d'évaluation</h4>
          </div>

          {/* Rubric content (rich text / table) - styled properly */}
          {assignmentDetail.rubricContent && (
            <div className="mb-4 overflow-x-auto">
              <style>{`
                .rubric-content {
                  font-size: 0.9rem;
                  line-height: 1.5;
                }
                .rubric-content table {
                  border-collapse: collapse;
                  width: 100%;
                  margin: 12px 0;
                  border-radius: 12px;
                  overflow: hidden;
                  border: 2px solid #e9d5ff;
                  box-shadow: 0 2px 8px rgba(147, 51, 234, 0.1);
                }
                .rubric-content th, .rubric-content td {
                  border: 1px solid #e9d5ff;
                  padding: 14px 18px;
                  text-align: left;
                  vertical-align: top;
                }
                .rubric-content th {
                  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
                  font-weight: 700;
                  color: #7c3aed;
                  font-size: 0.875rem;
                  text-transform: uppercase;
                  letter-spacing: 0.05em;
                }
                .rubric-content tbody tr {
                  background-color: white;
                }
                .rubric-content tbody tr:nth-child(even) {
                  background-color: #faf5ff;
                }
                .rubric-content tbody tr:hover {
                  background-color: #f3e8ff;
                }
                .rubric-content td {
                  color: #374151;
                }
                .rubric-content p {
                  margin: 8px 0;
                }
              `}</style>
              <div
                className="rubric-content"
                dangerouslySetInnerHTML={createSafeHtml(assignmentDetail.rubricContent)}
              />
            </div>
          )}

          {/* Rubric file attachment with inline preview */}
          {assignmentDetail.rubricUrl && assignmentDetail.rubricFileName && (() => {
            const rubricIcon = getFileIcon(assignmentDetail.rubricFileName);
            const rubricColorClass = getFileColor(assignmentDetail.rubricFileName);
            const rubricExt = getFileExtension(assignmentDetail.rubricFileName);
            const isRubricExpanded = isFileExpanded(assignmentDetail.rubricUrl);

            return (
              <div>
                <div className={`flex items-center gap-3 p-4 rounded-xl transition-colors ${
                  isRubricExpanded
                    ? 'bg-purple-100 border-2 border-purple-400'
                    : 'bg-purple-50 hover:bg-purple-100'
                }`}>
                  <div className={`p-3 rounded-lg ${rubricColorClass}`}>
                    {React.createElement(rubricIcon, { className: 'h-6 w-6' })}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{assignmentDetail.rubricFileName}</p>
                    <p className="text-xs text-gray-500">
                      {rubricExt ? rubricExt.toUpperCase() + ' Document' : 'Document de grille d\'évaluation'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isRubricExpanded && (
                      <button
                        onClick={() => handleExpandFile(assignmentDetail.rubricUrl!, assignmentDetail.rubricFileName!)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                      >
                        <Maximize2 className="h-4 w-4" />
                        Voir
                      </button>
                    )}
                    <button
                      onClick={() => handleDownload(assignmentDetail.rubricUrl!, assignmentDetail.rubricFileName!)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger
                    </button>
                  </div>
                </div>

                {/* Inline expanded preview for rubric */}
                {isRubricExpanded && renderExpandedPreviewInline()}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

interface AssignmentsTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted = the live one
  academicYearId?: string;
}

const DEFAULT_FILE_TYPES = ['PDF', 'DOC', 'DOCX', 'PPT', 'PPTX', 'XLS', 'XLSX', 'JPG', 'PNG', 'ZIP', 'RAR'];

const AssignmentsTab: React.FC<AssignmentsTabProps> = ({ groupeId, moduleElementId, academicYearId }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);
  const [gradingAssignmentId, setGradingAssignmentId] = useState<string | null>(null); // For submissions view
  const [gradingSubmissionId, setGradingSubmissionId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null); // For document preview

  // Form state with new fields
  const [formData, setFormData] = useState<CreateAssignmentDto & { rubricContent?: string }>({
    title: '',
    description: '',
    instructions: '',
    dueDate: '',
    maxScore: 20,
    allowLateSubmission: false,
    latePenaltyPercent: 10,
    lateSubmissionDays: 3,
    acceptedFileTypes: DEFAULT_FILE_TYPES,
    attachments: [],
    includeInFinalGrade: false,
    gradeComponentCode: undefined,
    gradeWeight: undefined,
    rubricUrl: undefined,
    rubricFileName: undefined,
    rubricContent: '',
  });

  // Grading form state
  const [gradingData, setGradingData] = useState<GradeSubmissionDto>({
    score: null,
    feedback: '',
  });

  // Queries
  const { data: assignments = [], isLoading } = useAssignments(groupeId, moduleElementId, academicYearId);
  const { data: assignmentDetail } = useAssignmentDetail(selectedAssignmentId);
  const { data: gradeConfig } = useGradeConfig(groupeId, moduleElementId);

  // Mutations
  const createMutation = useCreateAssignment();
  const updateMutation = useUpdateAssignment();
  const deleteMutation = useDeleteAssignment();
  const gradeMutation = useGradeSubmission();

  const handleCreate = async () => {
    if (!formData.title || !formData.dueDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      await createMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: formData,
      });
      toast.success('Devoir créé avec succès');
      setIsCreating(false);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la création du devoir');
    }
  };

  const handleUpdate = async (assignmentId: string) => {
    try {
      // When updating, clear legacy attachment fields and use only the attachments array
      const updateDto: UpdateAssignmentDto = {
        ...formData,
        // Clear legacy single attachment fields - all attachments are now in the array
        attachmentUrl: null,
        attachmentFileName: null,
      };

      await updateMutation.mutateAsync({
        assignmentId,
        groupeId,
        moduleElementId,
        dto: updateDto,
      });
      toast.success('Devoir mis à jour');
      setIsEditing(null);
      resetForm();
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (assignmentId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce devoir ? Cette action est irréversible.')) {
      return;
    }

    try {
      await deleteMutation.mutateAsync({
        assignmentId,
        groupeId,
        moduleElementId,
      });
      toast.success('Devoir supprimé');
      if (selectedAssignmentId === assignmentId) {
        setSelectedAssignmentId(null);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la suppression');
    }
  };

  const handleGradeSubmission = async (submissionId: string) => {
    try {
      await gradeMutation.mutateAsync({
        submissionId,
        assignmentId: selectedAssignmentId!,
        groupeId,
        moduleElementId,
        dto: gradingData,
      });
      toast.success('Note enregistrée');
      setGradingSubmissionId(null);
      setGradingData({ score: null, feedback: '' });
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la notation');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      instructions: '',
      dueDate: '',
      maxScore: 20,
      allowLateSubmission: false,
      latePenaltyPercent: 10,
      lateSubmissionDays: 3,
      acceptedFileTypes: DEFAULT_FILE_TYPES,
      attachments: [],
      includeInFinalGrade: false,
      gradeComponentCode: undefined,
      gradeWeight: undefined,
      rubricUrl: undefined,
      rubricFileName: undefined,
      rubricContent: '',
    });
  };

  const startEditing = (assignment: AssignmentResponse) => {
    // Combine legacy single attachment with array attachments for editing
    const existingAttachments: AttachmentFile[] = [];

    // Add legacy single attachment if present
    if (assignment.attachmentUrl && assignment.attachmentFileName) {
      existingAttachments.push({
        url: assignment.attachmentUrl,
        fileName: assignment.attachmentFileName,
      });
    }

    // Add array attachments
    if (assignment.attachments && assignment.attachments.length > 0) {
      existingAttachments.push(...assignment.attachments);
    }

    setFormData({
      title: assignment.title,
      description: assignment.description || '',
      instructions: assignment.instructions || '',
      dueDate: new Date(assignment.dueDate).toISOString().slice(0, 16),
      maxScore: assignment.maxScore,
      allowLateSubmission: assignment.allowLateSubmission,
      latePenaltyPercent: assignment.latePenaltyPercent || 10,
      lateSubmissionDays: assignment.lateSubmissionDays || 3,
      acceptedFileTypes: assignment.acceptedFileTypes || DEFAULT_FILE_TYPES,
      attachments: existingAttachments,
      includeInFinalGrade: assignment.includeInFinalGrade || false,
      gradeComponentCode: assignment.gradeComponentCode || undefined,
      gradeWeight: assignment.gradeWeight || undefined,
      rubricUrl: assignment.rubricUrl || undefined,
      rubricFileName: assignment.rubricFileName || undefined,
      rubricContent: assignment.rubricContent || '',
    });
    setIsEditing(assignment.id);
  };

  const getStatusBadge = (assignment: AssignmentResponse) => {
    if (assignment.isPastDue) {
      if (assignment.gradedCount === assignment.submissionsCount && assignment.submissionsCount > 0) {
        return { label: 'Terminé', color: 'bg-gray-100 text-gray-700', icon: CheckCircle };
      }
      return { label: 'À corriger', color: 'bg-amber-100 text-amber-700', icon: AlertCircle };
    }

    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilDue <= 2) {
      return { label: `${daysUntilDue}j restant${daysUntilDue > 1 ? 's' : ''}`, color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    }
    if (daysUntilDue <= 7) {
      return { label: `${daysUntilDue}j restants`, color: 'bg-amber-100 text-amber-700', icon: Clock };
    }
    return { label: 'En cours', color: 'bg-green-100 text-green-700', icon: Clock };
  };

  const isAutoZeroSubmission = (submission: SubmissionResponse) =>
    !submission.content && !submission.fileUrl;

  const getSubmissionStatusBadge = (submission: SubmissionResponse) => {
    // No content and no file = student never submitted; 0 was applied
    // automatically once the submission window closed
    if (isAutoZeroSubmission(submission)) {
      return { label: 'Non remis — 0 (auto)', color: 'bg-red-100 text-red-700' };
    }
    // Backend returns uppercase status (GRADED, SUBMITTED, etc.)
    const status = submission.status?.toUpperCase();
    if (status === 'GRADED' || submission.score !== null) {
      return { label: 'Noté', color: 'bg-green-100 text-green-700' };
    }
    if (submission.isLate) {
      return { label: `En retard (${submission.lateDays}j)`, color: 'bg-red-100 text-red-700' };
    }
    return { label: 'À corriger', color: 'bg-amber-100 text-amber-700' };
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Document preview modal
  const DocumentPreviewModal = ({ url, fileName, onClose }: { url: string; fileName: string; onClose: () => void }) => {
    // Detect type from the file name, falling back to the URL extension
    const nameForType = `${fileName} ${url.split('?')[0]}`.toLowerCase();
    const isPdf = /\.pdf(\s|$)/.test(nameForType);
    const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)(\s|$)/.test(nameForType);
    // Office documents render through the Microsoft viewer (file must be publicly reachable)
    const isOffice = /\.(ppt|pptx|doc|docx|xls|xlsx)(\s|$)/.test(nameForType);
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-gray-900">{fileName}</span>
            </div>
            <div className="flex items-center gap-2">
              <a
                href={url}
                download={fileName}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Download className="h-4 w-4" />
                Télécharger
              </a>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Ouvrir dans un nouvel onglet
              </a>
              <button
                onClick={onClose}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-4 bg-gray-100">
            {isPdf ? (
              <iframe
                src={url}
                className="w-full h-full min-h-[70vh] rounded-lg border border-gray-200"
                title={fileName}
              />
            ) : isImage ? (
              <div className="flex items-center justify-center h-full">
                <img src={url} alt={fileName} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
              </div>
            ) : isOffice ? (
              <iframe
                src={officeViewerUrl}
                className="w-full h-full min-h-[70vh] rounded-lg border border-gray-200"
                title={fileName}
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FileText className="h-16 w-16 mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aperçu non disponible</p>
                <p className="text-sm mt-1">Ce type de fichier ne peut pas être prévisualisé directement.</p>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir le fichier
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Assignment detail view (no submissions - just info) - with inline preview
  if (selectedAssignmentId && assignmentDetail && !gradingAssignmentId) {
    return (
      <AssignmentDetailViewInline
        assignmentDetail={assignmentDetail}
        selectedAssignmentId={selectedAssignmentId}
        onBack={() => setSelectedAssignmentId(null)}
        onGrade={() => setGradingAssignmentId(selectedAssignmentId)}
      />
    );
  }

  // Submissions/Grading view - separate from assignment detail
  if (gradingAssignmentId && assignmentDetail) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <button
            onClick={() => {
              setGradingAssignmentId(null);
              setGradingSubmissionId(null);
            }}
            className="text-sm text-blue-600 hover:text-blue-700 mb-4 flex items-center gap-1"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Retour au devoir
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3">
                <GraduationCap className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold text-gray-900">Correction: {assignmentDetail.title}</h3>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Note maximale: <span className="font-medium">{assignmentDetail.maxScore}</span>
                {assignmentDetail.allowLateSubmission && (
                  <span className="ml-4 text-amber-600">
                    Pénalité retard: -{assignmentDetail.latePenaltyPercent}%/jour
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">
                  {assignmentDetail.gradedCount}/{assignmentDetail.submissionsCount}
                </p>
                <p className="text-sm text-gray-500">soumissions notées</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-lg font-bold text-blue-600">
                  {assignmentDetail.submissionsCount > 0
                    ? Math.round((assignmentDetail.gradedCount / assignmentDetail.submissionsCount) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Submissions list */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">Soumissions des étudiants</h4>
            <span className="text-sm text-gray-500">
              {assignmentDetail.totalStudents - assignmentDetail.submissionsCount} étudiant(s) n'ont pas encore soumis
            </span>
          </div>

          {assignmentDetail.submissions.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Users className="mx-auto h-16 w-16 text-gray-300 mb-4" />
              <p className="text-lg font-medium">Aucune soumission</p>
              <p className="text-sm mt-1">Les étudiants n'ont pas encore soumis leur travail.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {assignmentDetail.submissions.map((submission) => {
                const statusBadge = getSubmissionStatusBadge(submission);
                const isGrading = gradingSubmissionId === submission.id;

                return (
                  <div key={submission.id} className={`p-4 ${isGrading ? 'bg-blue-50/50' : ''}`}>
                    <div className="flex items-start gap-4">
                      {/* Student info */}
                      <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-medium text-gray-600">
                          {submission.studentName.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-medium text-gray-900">{submission.studentName}</p>
                          <span className="text-sm text-gray-500">{submission.studentCode}</span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${statusBadge.color}`}>
                            {statusBadge.label}
                          </span>
                        </div>

                        {isAutoZeroSubmission(submission) ? (
                          <p className="text-sm text-red-600 mb-3">
                            Aucune soumission — 0 attribué automatiquement à la clôture du délai (modifiable ci-dessous)
                          </p>
                        ) : (
                          <p className="text-sm text-gray-500 mb-3">
                            Soumis le {new Date(submission.submittedAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}

                        {/* Submission file */}
                        {submission.fileUrl && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
                              <FileText className="h-4 w-4 text-blue-600" />
                              <span className="text-sm text-gray-700">{submission.fileName || 'Document'}</span>
                              {submission.fileSize && (
                                <span className="text-xs text-gray-400">
                                  ({(submission.fileSize / 1024 / 1024).toFixed(2)} MB)
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => setPreviewUrl(getFullUrl(submission.fileUrl))}
                              className="flex items-center gap-1 px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Eye className="h-4 w-4" />
                              Voir
                            </button>
                            <a
                              href={getFullUrl(submission.fileUrl)}
                              download
                              className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <Download className="h-4 w-4" />
                              Télécharger
                            </a>
                          </div>
                        )}

                        {/* Text content */}
                        {submission.content && (
                          <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 mb-3 max-h-32 overflow-y-auto">
                            {submission.content}
                          </div>
                        )}

                        {/* Grading form */}
                        {isGrading ? (
                          <div className="p-4 bg-white border border-blue-200 rounded-lg space-y-3">
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700">Note:</label>
                                <input
                                  type="number"
                                  value={gradingData.score ?? ''}
                                  onChange={(e) => setGradingData({
                                    ...gradingData,
                                    score: e.target.value ? parseFloat(e.target.value) : null,
                                  })}
                                  min="0"
                                  max={assignmentDetail.maxScore}
                                  step="0.5"
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-center font-medium"
                                  placeholder="0"
                                />
                                <span className="text-gray-500 font-medium">/ {assignmentDetail.maxScore}</span>
                              </div>
                              {submission.isLate && assignmentDetail.latePenaltyPercent && (
                                <span className="text-sm text-amber-600">
                                  Pénalité: -{submission.lateDays! * assignmentDetail.latePenaltyPercent}%
                                </span>
                              )}
                            </div>
                            <textarea
                              value={gradingData.feedback || ''}
                              onChange={(e) => setGradingData({ ...gradingData, feedback: e.target.value })}
                              placeholder="Commentaire pour l'étudiant (optionnel)"
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleGradeSubmission(submission.id)}
                                disabled={gradeMutation.isPending}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                              >
                                {gradeMutation.isPending ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4" />
                                )}
                                Enregistrer
                              </button>
                              <button
                                onClick={() => {
                                  setGradingSubmissionId(null);
                                  setGradingData({ score: null, feedback: '' });
                                }}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Existing feedback */}
                            {submission.feedback && (
                              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800 mb-3">
                                <span className="font-medium">Votre commentaire:</span> {submission.feedback}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      {/* Score and grade button */}
                      <div className="flex flex-col items-end gap-2">
                        {submission.score !== null ? (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900">
                              {submission.score}<span className="text-base text-gray-400">/{assignmentDetail.maxScore}</span>
                            </p>
                            {submission.gradedAt && (
                              <p className="text-xs text-gray-400">
                                Noté le {new Date(submission.gradedAt).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-xl text-gray-300">—/{assignmentDetail.maxScore}</span>
                        )}

                        {!isGrading && (
                          <button
                            onClick={() => {
                              setGradingSubmissionId(submission.id);
                              setGradingData({
                                score: submission.score,
                                feedback: submission.feedback || '',
                              });
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                          >
                            <Edit2 className="h-4 w-4" />
                            {submission.score !== null ? 'Modifier' : 'Noter'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Preview Modal */}
        {previewUrl && (
          <DocumentPreviewModal
            url={previewUrl}
            fileName={
              assignmentDetail.submissions.find(s => getFullUrl(s.fileUrl) === previewUrl)?.fileName ||
              'Document'
            }
            onClose={() => setPreviewUrl(null)}
          />
        )}
      </div>
    );
  }

  // Get assignments linked to same grade component for weight distribution
  const getAssignmentsForComponent = (componentCode: string) => {
    return assignments.filter(a =>
      a.includeInFinalGrade &&
      a.gradeComponentCode === componentCode &&
      a.id !== isEditing
    );
  };

  // Calculate total weight for a component
  const getTotalWeightForComponent = (componentCode: string) => {
    const linkedAssignments = getAssignmentsForComponent(componentCode);
    return linkedAssignments.reduce((sum, a) => sum + (a.gradeWeight || 0), 0);
  };

  // Create/Edit form - REDESIGNED
  if (isCreating || isEditing) {
    const linkedAssignments = formData.gradeComponentCode ? getAssignmentsForComponent(formData.gradeComponentCode) : [];
    const existingWeight = formData.gradeComponentCode ? getTotalWeightForComponent(formData.gradeComponentCode) : 0;
    const remainingWeight = 100 - existingWeight;

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              {isCreating ? 'Nouveau devoir' : 'Modifier le devoir'}
            </h3>
            <button
              onClick={() => {
                setIsCreating(false);
                setIsEditing(null);
                resetForm();
              }}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* SECTION 1: Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titre du devoir *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
              placeholder="Ex: Analyse de cas - Marketing Digital"
            />
          </div>

          {/* SECTION 2: Instructions avec éditeur riche */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-gray-900">Instructions</h4>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Décrivez les consignes et instructions pour les étudiants. Utilisez la barre d'outils pour formater le texte.
            </p>
            <RichTextEditor
              key={`instructions-${isEditing || 'new'}`}
              value={formData.instructions || ''}
              onChange={(value) => setFormData({ ...formData, instructions: value })}
              placeholder="Écrivez les instructions ici... Vous pouvez utiliser des listes, du texte en gras, etc."
              minRows={5}
            />
          </div>

          {/* SECTION 3: Documents joints */}
          <div className="border border-gray-200 rounded-xl p-4 bg-gray-50/50">
            <div className="flex items-center gap-2 mb-3">
              <Paperclip className="h-5 w-5 text-green-600" />
              <h4 className="font-medium text-gray-900">Documents joints</h4>
              <span className="text-xs text-gray-400">(optionnel)</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Ajoutez des documents à partager avec les étudiants (énoncé, datasets, ressources, etc.)
            </p>
            <FileUploadZone
              onFileSelect={(file) => {
                setFormData({
                  ...formData,
                  attachments: [
                    ...(formData.attachments || []),
                    { fileName: file.name, url: file.url, fileType: file.type, fileSize: file.size }
                  ]
                });
              }}
              existingFiles={formData.attachments || []}
              onRemoveFile={(index) => {
                const newAttachments = [...(formData.attachments || [])];
                newAttachments.splice(index, 1);
                setFormData({ ...formData, attachments: newAttachments });
              }}
              hint="Glissez-déposez vos fichiers ici ou cliquez pour sélectionner"
            />
          </div>

          {/* SECTION 4: Grille d'évaluation (Rubric) */}
          <div className="border border-purple-200 rounded-xl p-4 bg-purple-50/30">
            <div className="flex items-center gap-2 mb-3">
              <Scale className="h-5 w-5 text-purple-600" />
              <h4 className="font-medium text-gray-900">Grille d'évaluation</h4>
              <span className="text-xs text-gray-400">(optionnel)</span>
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Définissez les critères d'évaluation. Vous pouvez utiliser le tableau pour structurer votre grille ou joindre un document.
            </p>

            {/* Rubric Rich Text Editor */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Critères d'évaluation (texte)</label>
              <RichTextEditor
                key={`rubric-${isEditing || 'new'}`}
                value={formData.rubricContent || ''}
                onChange={(value) => setFormData({ ...formData, rubricContent: value })}
                placeholder="Cliquez sur l'icône tableau pour insérer une grille d'évaluation, ou décrivez vos critères..."
                minRows={4}
              />
            </div>

            {/* Rubric File Upload */}
            <div className="border-t border-purple-200 pt-4">
              <label className="block text-sm text-gray-600 mb-2">Ou joindre un document de grille d'évaluation</label>

              {/* Display existing rubric file first */}
              {formData.rubricUrl && formData.rubricFileName && (
                <div className="mb-3 flex items-center gap-2 p-2 bg-white rounded-lg border border-purple-200">
                  <FileText className="h-4 w-4 text-purple-600" />
                  <span className="flex-1 text-sm text-gray-700">{formData.rubricFileName}</span>
                  <a
                    href={getFullUrl(formData.rubricUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, rubricUrl: undefined, rubricFileName: undefined })}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Drag & Drop zone for rubric */}
              <RubricUploadZone
                onFileSelect={(file) => {
                  setFormData({
                    ...formData,
                    rubricFileName: file.name,
                    rubricUrl: file.url
                  });
                }}
                hasFile={!!(formData.rubricUrl && formData.rubricFileName)}
              />
            </div>
          </div>

          {/* SECTION 5: Paramètres */}
          <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="h-5 w-5 text-blue-600" />
              <h4 className="font-medium text-gray-900">Paramètres</h4>
            </div>

            <div className="space-y-5">
              {/* Grade Integration */}
              <div className="p-4 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <Award className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-gray-800">Intégration à la note finale</span>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="includeInGrade"
                    checked={formData.includeInFinalGrade || false}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        includeInFinalGrade: e.target.checked,
                        gradeComponentCode: e.target.checked ? formData.gradeComponentCode : undefined,
                        gradeWeight: e.target.checked ? formData.gradeWeight : undefined
                      });
                    }}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="includeInGrade" className="text-sm text-gray-700">
                    Ce devoir compte dans la note finale
                  </label>
                </div>

                {formData.includeInFinalGrade && (
                  <div className="space-y-4 pl-6 pt-2 border-t border-gray-100">
                    {gradeConfig?.isValid && gradeConfig.components.length > 0 ? (
                      <>
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">
                            Composante de note
                          </label>
                          <select
                            value={formData.gradeComponentCode || ''}
                            onChange={(e) => {
                              const newCode = e.target.value || undefined;
                              // Auto-set weight to remaining weight if only one assignment for this component
                              const newLinked = newCode ? assignments.filter(a =>
                                a.includeInFinalGrade &&
                                a.gradeComponentCode === newCode &&
                                a.id !== isEditing
                              ) : [];
                              const newExisting = newLinked.reduce((sum, a) => sum + (a.gradeWeight || 0), 0);
                              const newRemaining = 100 - newExisting;

                              setFormData({
                                ...formData,
                                gradeComponentCode: newCode,
                                gradeWeight: newLinked.length === 0 ? 100 : Math.min(formData.gradeWeight || 50, newRemaining)
                              });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          >
                            <option value="">-- Sélectionner --</option>
                            {gradeConfig.components.map((comp) => (
                              <option key={comp.code} value={comp.code}>
                                {comp.name} ({comp.percentage}% de la note finale)
                              </option>
                            ))}
                          </select>
                        </div>

                        {formData.gradeComponentCode && (
                          <div className="space-y-3">
                            {linkedAssignments.length > 0 && (
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-sm text-amber-800 font-medium mb-2">
                                  {linkedAssignments.length} autre(s) devoir(s) lié(s) à cette composante:
                                </p>
                                <ul className="text-sm text-amber-700 space-y-1">
                                  {linkedAssignments.map(a => (
                                    <li key={a.id} className="flex justify-between">
                                      <span>{a.title}</span>
                                      <span className="font-medium">{a.gradeWeight}%</span>
                                    </li>
                                  ))}
                                </ul>
                                <p className="text-xs text-amber-600 mt-2">
                                  Poids restant disponible: <span className="font-bold">{remainingWeight}%</span>
                                </p>
                              </div>
                            )}

                            <div>
                              <label className="block text-sm text-gray-600 mb-1">
                                Poids de ce devoir dans la composante
                              </label>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  value={formData.gradeWeight || (linkedAssignments.length === 0 ? 100 : '')}
                                  onChange={(e) => setFormData({ ...formData, gradeWeight: parseFloat(e.target.value) || undefined })}
                                  min="1"
                                  max={remainingWeight}
                                  placeholder={linkedAssignments.length === 0 ? "100" : `Max: ${remainingWeight}`}
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                />
                                <span className="text-sm text-gray-500">%</span>
                                {linkedAssignments.length === 0 && (
                                  <span className="text-xs text-green-600">
                                    (Seul devoir = 100% automatique)
                                  </span>
                                )}
                              </div>
                              {linkedAssignments.length > 0 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Le total des poids doit égaler 100%. Les devoirs seront pondérés proportionnellement.
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="text-sm text-amber-700">
                          Veuillez d'abord configurer les composantes de notation dans l'onglet "Notes"
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Deadline */}
              <div className="p-4 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-gray-800">Date limite</span>
                </div>
                <input
                  type="datetime-local"
                  value={formData.dueDate}
                  onChange={(e) => {
                    // A date picked without a time lands on 00:00, which made the
                    // whole due day count as late: default the time to 23:59.
                    const value = e.target.value;
                    const dueDate = value.endsWith('T00:00') ? `${value.slice(0, 11)}23:59` : value;
                    setFormData({ ...formData, dueDate });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">Les étudiants peuvent rendre jusqu'à l'heure indiquée. Sans heure précise, la limite est 23:59.</p>
              </div>

              {/* Late Submissions */}
              <div className="p-4 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-gray-800">Soumissions en retard</span>
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="allowLate"
                    checked={formData.allowLateSubmission}
                    onChange={(e) => setFormData({ ...formData, allowLateSubmission: e.target.checked })}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="allowLate" className="text-sm text-gray-700">
                    Accepter les soumissions après la date limite
                  </label>
                </div>

                {formData.allowLateSubmission && (
                  <div className="grid grid-cols-2 gap-4 pl-6 pt-2 border-t border-gray-100">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Nombre de jours acceptés
                      </label>
                      <input
                        type="number"
                        value={formData.lateSubmissionDays}
                        onChange={(e) => setFormData({ ...formData, lateSubmissionDays: parseInt(e.target.value) || 3 })}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        Pénalité par jour (%)
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={formData.latePenaltyPercent}
                          onChange={(e) => setFormData({ ...formData, latePenaltyPercent: parseInt(e.target.value) || 10 })}
                          min="0"
                          max="100"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <span className="text-sm text-gray-500">%</span>
                      </div>
                    </div>
                    <div className="col-span-2 p-2 bg-gray-50 rounded-lg text-xs text-gray-600">
                      <strong>Exemple:</strong> Si un étudiant obtient 10/20 et soumet avec 1 jour de retard (pénalité {formData.latePenaltyPercent || 10}%),
                      sa note sera: {(10 - (10 * (formData.latePenaltyPercent || 10) / 100)).toFixed(1)}/20
                    </div>
                  </div>
                )}
              </div>

              {/* Accepted File Types */}
              <div className="p-4 bg-white rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-blue-600" />
                  <span className="font-medium text-gray-800">Types de fichiers acceptés</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Sélectionnez les formats que les étudiants peuvent soumettre
                </p>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_FILE_TYPES.map((type) => (
                    <label
                      key={type}
                      className={`
                        flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer transition-colors
                        ${formData.acceptedFileTypes?.includes(type)
                          ? 'bg-green-100 border-green-400 text-green-700 font-medium'
                          : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                        }
                      `}
                    >
                      <input
                        type="checkbox"
                        checked={formData.acceptedFileTypes?.includes(type) ?? true}
                        onChange={(e) => {
                          const current = formData.acceptedFileTypes || DEFAULT_FILE_TYPES;
                          if (e.target.checked) {
                            setFormData({ ...formData, acceptedFileTypes: [...current, type] });
                          } else {
                            setFormData({ ...formData, acceptedFileTypes: current.filter(t => t !== type) });
                          }
                        }}
                        className="sr-only"
                      />
                      <span className="text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setIsCreating(false);
                setIsEditing(null);
                resetForm();
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => isEditing ? handleUpdate(isEditing) : handleCreate()}
              disabled={!formData.title || !formData.dueDate || createMutation.isPending || updateMutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {isCreating ? 'Créer le devoir' : 'Enregistrer les modifications'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Assignments list view
  return (
    <div className="space-y-6">
      {/* Create button */}
      <button
        onClick={() => setIsCreating(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Nouveau devoir
      </button>

      {/* Empty state */}
      {assignments.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <ClipboardList className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun devoir</h3>
          <p className="mt-2 text-sm text-gray-500">
            Créez un devoir pour vos étudiants
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Upcoming assignments */}
          {assignments.filter(a => !a.isPastDue).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                À venir
              </h4>
              <div className="space-y-3">
                {assignments.filter(a => !a.isPastDue).map((assignment) => (
                  <AssignmentCard
                    key={assignment.id}
                    assignment={assignment}
                    onView={() => setSelectedAssignmentId(assignment.id)}
                    onGrade={() => {
                      setSelectedAssignmentId(assignment.id);
                      setGradingAssignmentId(assignment.id);
                    }}
                    onEdit={() => startEditing(assignment)}
                    onDelete={() => handleDelete(assignment.id)}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Past due assignments */}
          {assignments.filter(a => a.isPastDue).length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                {assignments.filter(a => a.isPastDue && a.gradedCount < a.submissionsCount).length > 0
                  ? 'En cours de correction'
                  : 'Terminés'}
              </h4>
              <div className="space-y-3">
                {assignments.filter(a => a.isPastDue).map((assignment) => (
                  <AssignmentCard
                    key={assignment.id}
                    assignment={assignment}
                    onView={() => setSelectedAssignmentId(assignment.id)}
                    onGrade={() => {
                      setSelectedAssignmentId(assignment.id);
                      setGradingAssignmentId(assignment.id);
                    }}
                    onEdit={() => startEditing(assignment)}
                    onDelete={() => handleDelete(assignment.id)}
                    getStatusBadge={getStatusBadge}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Assignment card component
interface AssignmentCardProps {
  assignment: AssignmentResponse;
  onView: () => void;
  onGrade: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getStatusBadge: (a: AssignmentResponse) => { label: string; color: string; icon: any };
}

const AssignmentCard: React.FC<AssignmentCardProps> = ({
  assignment,
  onView,
  onGrade,
  onEdit,
  onDelete,
  getStatusBadge,
}) => {
  const status = getStatusBadge(assignment);
  const StatusIcon = status.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-gray-900">{assignment.title}</h4>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${status.color}`}>
                <StatusIcon className="h-3 w-3" />
                {status.label}
              </span>
            </div>
            {assignment.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{assignment.description}</p>
            )}
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {new Date(assignment.dueDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {assignment.submissionsCount} soumission{assignment.submissionsCount > 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4" />
                {assignment.gradedCount} noté{assignment.gradedCount > 1 ? 's' : ''}
              </span>
              <span>/{assignment.maxScore} pts</span>
            </div>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              {assignment.allowLateSubmission && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">
                  <Clock className="h-3 w-3" />
                  Retards acceptés ({assignment.lateSubmissionDays}j, -{assignment.latePenaltyPercent}%/j)
                </span>
              )}
              {(assignment.attachmentUrl || (assignment.attachments && assignment.attachments.length > 0)) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                  <FileText className="h-3 w-3" />
                  {assignment.attachments && assignment.attachments.length > 0
                    ? `${assignment.attachments.length + (assignment.attachmentUrl ? 1 : 0)} fichier(s) joint(s)`
                    : 'Document joint'}
                </span>
              )}
              {assignment.includeInFinalGrade && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                  <Award className="h-3 w-3" />
                  Noté ({assignment.gradeComponentCode || `${assignment.gradeWeight}%`})
                </span>
              )}
              {assignment.rubricUrl && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">
                  <Scale className="h-3 w-3" />
                  Grille d'évaluation
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onView}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Voir le devoir"
            >
              <Eye className="h-4 w-4" />
              Voir
            </button>
            <button
              onClick={onGrade}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                assignment.submissionsCount > assignment.gradedCount
                  ? 'text-white bg-amber-500 hover:bg-amber-600'
                  : 'text-green-600 hover:bg-green-50'
              }`}
              title="Corriger les soumissions"
            >
              <GraduationCap className="h-4 w-4" />
              {assignment.gradedCount}/{assignment.submissionsCount}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Modifier"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentsTab;
